import { debounce } from 'lodash';
import {
  CSSProperties, useEffect, useRef, useState,
} from 'react';

import { isColorGradient } from '@typedream/core';
import sharedStyles from '../Shared.module.css';
import { MenuSectionVertical } from '../Menu/MenuSectionVertical';
import { ColorInput } from '../../../components/ColorPicker';
import { LeftMenuItem } from '../LeftMenu';

type Color = [name: string, value: string];

interface ColorOptionProps {
  color: Color
  iconStyle?: 'text' | 'background'
  onClick: () => void,
  testIdPrefix?: string,
}

function ColorOption(props: ColorOptionProps) {
  const {
    color: [name, value],
    iconStyle,
    onClick, testIdPrefix,
  } = props;

  let style: CSSProperties = { color: value };
  if (iconStyle === 'background') {
    style = { background: value };
  } else if (iconStyle === 'text' && isColorGradient(name)) {
    style = {
      backgroundImage: value,
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      boxDecorationBreak: 'clone',
      WebkitBoxDecorationBreak: 'clone',
      fontWeight: 900,
    };
  }

  return (
    <LeftMenuItem
      icon={(
        <div
          className={sharedStyles.colorIconBackground}
          style={style}
          data-testid={`${testIdPrefix}${name}`}
        >
          T
        </div>
      )}
      title={name}
      onClick={onClick}
    />
  );
}

ColorOption.defaultProps = {
  iconStyle: 'text',
  testIdPrefix: undefined,
};

interface SelectColorProps {
  title?: string
  onSelect?: (arg0: string) => void
  currentColor?: string
  colors: Array<ColorOptionProps['color'] | ColorOptionProps['color'][]>
  iconStyle?: ColorOptionProps['iconStyle']
  disableCustomColor?: boolean,
  testIdPrefix?: string
}

function SelectColorNavbar(props: SelectColorProps) {
  const {
    title,
    onSelect,
    colors,
    disableCustomColor,
    currentColor,
    iconStyle,
    testIdPrefix,
  } = props;

  const [menuMaxHeight, setMenuMaxHeight] = useState<string>();

  const titleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const calculateAndSetMaxHeight = debounce(() => {
      const top = titleRef?.current?.getBoundingClientRect()?.top;
      setMenuMaxHeight(`calc(100vh - ${(top || 0) + 100}px)`);
    }, 150);

    window.addEventListener('scroll', calculateAndSetMaxHeight);

    // this is hack to set maxHeight properly when it first enter viewport
    // it's when color option menu is clicked.
    // Can't set it on first mount, because at the first mount
    // the component is at position top around -10000px
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].intersectionRatio === 1) {
          calculateAndSetMaxHeight();
        }
      },
      { threshold: [1] },
    );

    if (titleRef.current) {
      observer.observe(titleRef.current);
    }

    return () => {
      window.removeEventListener('scroll', calculateAndSetMaxHeight);
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <div className={sharedStyles.optionTitle} ref={titleRef}>
        {title || ''}
      </div>
      <div
        style={{
          maxHeight: menuMaxHeight,
          transition: 'max-height 0.3s',
          overflowY: 'scroll',
        }}
      >
        {colors.map((color, index) => {
          // group of colors
          if (Array.isArray(color[0])) {
            return (
              // using index as key here is safe, because the order will never change
              // eslint-disable-next-line react/no-array-index-key
              <MenuSectionVertical key={index}>
                {(color as Color[]).map((c) => (
                  <ColorOption
                    key={c[0]}
                    color={c}
                    onClick={() => onSelect?.(c[0])}
                    iconStyle={iconStyle}
                    testIdPrefix={testIdPrefix}
                  />
                ))}
              </MenuSectionVertical>
            );
          }

          return (
            <ColorOption
              key={color[0]}
              color={color as Color}
              onClick={() => onSelect?.(color[0] as string)}
              iconStyle={iconStyle}
              testIdPrefix={testIdPrefix}
            />
          );
        })}
      </div>
      {!disableCustomColor && (
        <MenuSectionVertical>
          <ColorInput
            predefinedColors={{}}
            value={currentColor}
            onSubmit={onSelect}
          />
        </MenuSectionVertical>
      )}
    </>
  );
}

SelectColorNavbar.defaultProps = {
  title: undefined,
  onSelect: () => {
    /* do nothing */
  },
  disableCustomColor: false,
  currentColor: '',
  iconStyle: 'text',
  testIdPrefix: undefined,
};

export default SelectColorNavbar;
